import { put, takeLatest, call, delay } from "redux-saga/effects";
import IngresoActions, {
  Types as IngresoTypes,
} from "../../stores/ingreso/Actions";
import IngresoService from "../../services/ingreso/IngresoService";
import moment from "moment";
import { DATE_FORMATS, PSS_TYPE } from "../../utils/Constant";
import { getAge, hasDateExceededXdays } from "../../utils/DateUtils";
import ResponseCode from "../../services/ResponseCode";
import {
  obtenerObjetoPoliza,
  obtenerSiguienteSecuencia,
} from "../../containers/ingreso/utils/IngresoUtils";
import {
  DEFAULT_ROW_PROCEDIMIENTOS_INGRESO,
  MAXIMO_DIAS_PERMITIDOS_CIERRE,
  MENSAJE_CIERRE_CUENTA,
  RAMO_INGRESO,
  SERVICIO_EXCEPCION,
  USUARIO_WEB,
  ESTATUS_COBERTURA,
  COMENTARIO_POR_DEFECTO,
  CAUSA_POR_DEFECTO,
  TIPO_SERVICIO_PERMITIDO,
  ESTATUS_PERMITIDO,
  ESTATUS_INGRESO,
  CONFIGURACION_COBERTURAS_NO_PERMITIDAS,
} from "../../containers/ingreso/utils/IngresoConstant";
import { isEmptyValue } from "../../utils/StringUtil";

export function* formCierreCuentas(request) {
  yield put(IngresoActions.formCierreCuentasLoading());

  yield put(IngresoActions.formCierreCuentasSuccess(request?.payload));
}

export function* formConsultaCierresCuentas(request) {
  yield put(IngresoActions.formConsultaCierresCuentasLoading());

  yield put(IngresoActions.formConsultaCierresCuentasSuccess(request?.payload));
}

function* consultarIngreso(request) {
  yield put(IngresoActions.consultarIngresoLoading());

  const {
    formCierreCuentasData,
    numeroIngreso,
    reclamante,
    formHandler,
    compania,
    ano,
  } = request.payload;

  const requestConsultarIngreso = {
    ano: ano,
    compania: compania,
    ramo: RAMO_INGRESO.PRIMERA,
    secuencial: numeroIngreso,
    reclamante: reclamante?.codigo,
  };

  const ingresoResponse = yield call(
    IngresoService.obtenerIngreso,
    requestConsultarIngreso
  );

  if (ingresoResponse?.data?.ingresoEncabezado) {
    const { ingresoEncabezado, ingresoDetalle } = ingresoResponse.data;

    const ingresoConPrecertificacion =
      ingresoEncabezado?.numeroPrecertificacion != null;

    if (!ingresoConPrecertificacion) {
      yield put(
        IngresoActions.formCierreCuentasSuccess({
          ...formCierreCuentasData,
          alertConfig: {
            type: "info",
            title: "¡Hola!",
            message: MENSAJE_CIERRE_CUENTA.INGRESO_NO_EXISTE,
            onConfirm: () => {
              formHandler({
                ...formCierreCuentasData,
                showAlert: false,
              });
            },
          },
          showAlert: true,
        })
      );

      return;
    }

    if (ingresoEncabezado?.estatusDesc?.toUpperCase() !== ESTATUS_PERMITIDO) {
      yield put(
        IngresoActions.formCierreCuentasSuccess({
          ...formCierreCuentasData,
          alertConfig: {
            type: "info",
            title: "¡Hola!",
            message: `No está permitido realizar un cierre de cuentas a un ingreso con estado: ${
              ingresoEncabezado?.estatusDesc?.toUpperCase() ===
              ESTATUS_INGRESO.CONVERTIDO
                ? "Cuenta Reportada"
                : ingresoEncabezado?.estatusDesc
            }.`,
            onConfirm: () => {
              formHandler({
                ...formCierreCuentasData,
                showAlert: false,
              });
            },
          },
          showAlert: true,
        })
      );

      return;
    }

    const tipoServicioPermitido = TIPO_SERVICIO_PERMITIDO.some(
      (item) => String(item.value) === String(ingresoEncabezado?.codigoServicio)
    );

    if (!tipoServicioPermitido) {
      yield put(
        IngresoActions.formCierreCuentasSuccess({
          ...formCierreCuentasData,
          alertConfig: {
            type: "info",
            title: "¡Hola!",
            message: `Los cierres de cuenta para el tipo de servicio ${ingresoEncabezado?.descripcionServicio} no están permitidos por esta vía.`,
            onConfirm: () => {
              formHandler({
                ...formCierreCuentasData,
                showAlert: false,
              });
            },
          },
          showAlert: true,
        })
      );

      return;
    }

    if (
      hasDateExceededXdays(
        ingresoEncabezado?.fechaIngreso,
        MAXIMO_DIAS_PERMITIDOS_CIERRE.GENERAL
      )
    ) {
      yield put(
        IngresoActions.formCierreCuentasSuccess({
          ...formCierreCuentasData,
          alertConfig: {
            type: "info",
            title: "¡Hola!",
            message: MENSAJE_CIERRE_CUENTA.DIAS_MAXIMOS_PERMITIDOS,
            onConfirm: () => {
              formHandler({
                ...formCierreCuentasData,
                showAlert: false,
              });
            },
          },
          showAlert: true,
        })
      );

      return;
    }

    if (ingresoEncabezado?.numeroReclamacion) {
      yield put(
        IngresoActions.formCierreCuentasSuccess({
          ...formCierreCuentasData,
          alertConfig: {
            type: "info",
            title: "¡Hola!",
            message: `El cierre de cuentas para el ingreso ${numeroIngreso} ya fue completado con la reclamación ${ingresoEncabezado?.numeroReclamacion}`,
            onConfirm: () => {
              formHandler({
                ...formCierreCuentasData,
                showAlert: false,
              });
            },
          },
          showAlert: true,
        })
      );

      return;
    }

    let procedimientosIngreso = [DEFAULT_ROW_PROCEDIMIENTOS_INGRESO];

    if (ingresoDetalle?.length > 0) {
      const procedimientosData = ingresoDetalle?.map((ingreso, index) => {
        const secuencia = index + 1;
        return {
          secuencia,
          key: ingreso.cobertura,
          cobertura: ingreso.cobertura,
          nombreCobertura: ingreso.descripcionCobertura,
          tipoReclamante: ingreso.tipoCentroMedico,
          codigoReclamante: ingreso.codigoCentroMedico,
          nombreReclamante: ingreso.nombreCentro,
          limiteContrato: ingreso.limiteCobertura || 0,
          frecuencia: ingreso.frecuencia,
          montoReclamado: ingreso.montoReclamadoCobertura || 0,
          montoAutorizado: ingreso.montoPagoCobertura || 0,
          montoDiferencia: ingreso.montoPagoAfiliado || 0,
          coaseguro: ingreso.porcientoCobertura || 0,
          honorarios: ingreso.excedenteCopago || 0,
          esNuevo: false,
        };
      });

      procedimientosIngreso = procedimientosIngreso.concat(procedimientosData);
    }

    const objetoPoliza = obtenerObjetoPoliza(ingresoEncabezado?.poliza);

    yield put(
      IngresoActions.formCierreCuentasSuccess({
        ...formCierreCuentasData,
        numeroIngreso: numeroIngreso,
        secuencialIngreso: ingresoEncabezado?.secuencial,
        fechaSalida: null,
        motivoEgreso: ingresoEncabezado?.motivoEgreso,
        causaInternamiento: CAUSA_POR_DEFECTO,
        categoriaPss: ingresoEncabezado?.categoria,
        numeroReclamacion: ingresoEncabezado?.numeroReclamacion,
        numeroPrecertificacion: ingresoEncabezado?.numeroPrecertificacion,
        descripcionEstatus: ingresoEncabezado?.estatusDesc,
        numeroAfiliado: ingresoEncabezado?.numeroPlan,
        cedula: ingresoEncabezado?.cedula,
        nss: ingresoEncabezado?.nss,
        nombreAfiliado: ingresoEncabezado?.nombreAfiliado,
        edad: getAge(ingresoEncabezado?.fechaNacimiento),
        fechaServicio: moment(ingresoEncabezado?.fechaIngreso).format(
          DATE_FORMATS.middledash_ymd
        ),
        tipoServicio: String(ingresoEncabezado?.codigoServicio),
        plan: ingresoEncabezado?.descripcionPlan,
        codigoPlan: ingresoEncabezado?.planPoliza,
        anio: ingresoEncabezado?.anio,
        compania: objetoPoliza?.compania,
        ramo: objetoPoliza?.ramo,
        secuenciaPoliza: objetoPoliza?.secuencial,
        codigoAsegurado: ingresoEncabezado?.codigoAsegurado,
        numeroPlastico: ingresoEncabezado?.numeroPlan,
        secuenciaAsegurado: ingresoEncabezado?.frecuenciaAsegurado,
        tipoAsegurado: ingresoEncabezado?.tipoAsegurado,
        fechaNacimiento: ingresoEncabezado?.fechaNacimiento,
        sexo: ingresoEncabezado?.sexo,
        estadoCivil: ingresoEncabezado?.estadoCivil,
        categoriaAfiliado: ingresoEncabezado?.categoriaAfiliado,
        procedimientos: procedimientosIngreso,
        tienePrecertificacion:
          ingresoEncabezado?.numeroPrecertificacion != null,
        aplicaExcepcion:
          Number(ingresoEncabezado?.codigoServicio) ===
            SERVICIO_EXCEPCION.PARTO ||
          Number(ingresoEncabezado?.codigoServicio) ===
            SERVICIO_EXCEPCION.CESAREA,
        showDetalleIngreso: true,
      })
    );
  } else {
    yield put(
      IngresoActions.formCierreCuentasSuccess({
        ...formCierreCuentasData,
        alertConfig: {
          type: "info",
          title: "¡Hola!",
          message: MENSAJE_CIERRE_CUENTA.INGRESO_NO_EXISTE,
          onConfirm: () => {
            formHandler({
              ...formCierreCuentasData,
              showAlert: false,
            });
          },
        },
        showAlert: true,
      })
    );
  }
}

function* getProcedimientosPermitidosCierreCuentas(request) {
  yield put(IngresoActions.getProcedimientosPermitidosCierreCuentasLoading());

  const { formCierreCuentasData, compania, formHandler } = request.payload;

  const { tipoPss, codigoPss, tipoServicio, codigoPlan } =
    formCierreCuentasData;

  const requestProcedimientos = {
    reclamante: codigoPss,
    tipoReclamante: tipoPss,
    compania: compania,
    servicio: tipoServicio,
    plan: codigoPlan,
  };

  const response = yield call(
    IngresoService.consultarCoberturasCierre,
    requestProcedimientos
  );

  if (
    !response?.data?.coberturasCierresCuentas ||
    response?.data?.coberturasCierresCuentas?.length === 0
  ) {
    yield put(
      IngresoActions.formCierreCuentasSuccess({
        ...formCierreCuentasData,
        alertConfig: {
          type: "info",
          title: "¡Hola!",
          message: "No se encontraron procedimientos permitidos.",
          onConfirm: () => {
            formHandler({
              ...formCierreCuentasData,
              showAlert: false,
            });
          },
        },
        showAlert: true,
      })
    );

    return;
  }

  const procedimientosPermitidos =
    response?.data?.coberturasCierresCuentas?.map((procedimiento) => {
      return {
        key: procedimiento.cobertura,
        cobertura: String(procedimiento.cobertura),
        nombreCobertura: procedimiento.descripcionCobertura,
        limiteContrato: procedimiento.limite || 0,
        tipoCobertura: procedimiento?.tipoCobertura,
      };
    });

  const procedimientosUnicos = [
    ...new Map(
      procedimientosPermitidos.map((item) => [item.cobertura, item])
    ).values(),
  ];

  yield put(
    IngresoActions.formCierreCuentasSuccess({
      ...formCierreCuentasData,
      procedimientosPermitidos: procedimientosUnicos,
    })
  );
}

function* validarProcedimientoCierreCuentas(request) {
  yield put(IngresoActions.validarProcedimientoCierreCuentasLoading());

  const {
    formCierreCuentasData,
    formHandler,
    codigoProcedimiento,
    frecuencia,
  } = request.payload;

  const {
    tipoPss,
    codigoPss,
    nombrePss,
    tipoServicio,
    codigoPlan,
    anio,
    compania,
    ramo,
    secuencialIngreso,
    secuenciaPoliza,
    codigoAsegurado,
    secuenciaAsegurado,
    tipoAsegurado,
    sexo,
    estadoCivil,
    fechaNacimiento,
    fechaSalida,
    fechaServicio,
    categoriaAfiliado,
    montoReclamado,
    tienePrecertificacion,
  } = formCierreCuentasData;
  if (
    formCierreCuentasData?.procedimientos?.some(
      (proc) =>
        Number(proc.cobertura) === Number(codigoProcedimiento) &&
        tipoPss?.toUpperCase() === proc.tipoReclamante?.toUpperCase() &&
        Number(proc.codigoReclamante) === Number(codigoPss)
    )
  ) {
    yield put(
      IngresoActions.formCierreCuentasSuccess({
        ...formCierreCuentasData,
        alertConfig: {
          type: "info",
          title: "¡Hola!",
          message: `El procedimiento ${codigoProcedimiento} ya fue agregado.`,
          onConfirm: () => {
            formHandler({
              ...formCierreCuentasData,
              showAlert: false,
            });
          },
        },
        showAlert: true,
      })
    );

    return;
  }

  if (
    Number(tipoServicio) === SERVICIO_EXCEPCION.HOSPITALIZACION &&
    !tienePrecertificacion
  ) {
    yield put(
      IngresoActions.formCierreCuentasSuccess({
        ...formCierreCuentasData,
        alertConfig: {
          type: "info",
          title: "¡Hola!",
          message: MENSAJE_CIERRE_CUENTA.COBERTURA_SERVICIO_NO_PERMITIDA,
          onConfirm: () => {
            formHandler({
              ...formCierreCuentasData,
              showAlert: false,
            });
          },
        },
        showAlert: true,
      })
    );

    return;
  }

  const esPartoOcesarea =
    Number(tipoServicio) === SERVICIO_EXCEPCION.PARTO ||
    Number(tipoServicio) === SERVICIO_EXCEPCION.CESAREA;

  if (
    CONFIGURACION_COBERTURAS_NO_PERMITIDAS.includes(
      Number(codigoProcedimiento)
    ) &&
    (esPartoOcesarea || tienePrecertificacion)
  ) {
    yield put(
      IngresoActions.formCierreCuentasSuccess({
        ...formCierreCuentasData,
        alertConfig: {
          type: "info",
          title: "¡Hola!",
          message: MENSAJE_CIERRE_CUENTA.COBERTURA_SERVICIO_NO_PERMITIDA,
          onConfirm: () => {
            formHandler({
              ...formCierreCuentasData,
              showAlert: false,
            });
          },
        },
        showAlert: true,
      })
    );

    return;
  }

  const requestProcedimientosPermitidos = {
    reclamante: codigoPss,
    tipoReclamante: tipoPss,
    compania: compania,
    servicio: tipoServicio,
    plan: codigoPlan,
  };

  const responseProcedimientosPermitidos = yield call(
    IngresoService.consultarCoberturasCierre,
    requestProcedimientosPermitidos
  );

  if (
    !responseProcedimientosPermitidos?.data?.coberturasCierresCuentas ||
    responseProcedimientosPermitidos?.data?.coberturasCierresCuentas?.length ===
      0
  ) {
    yield put(
      IngresoActions.formCierreCuentasSuccess({
        ...formCierreCuentasData,
        alertConfig: {
          type: "info",
          title: "¡Hola!",
          message: MENSAJE_CIERRE_CUENTA.COBERTURA_SERVICIO_NO_PERMITIDA,
          onConfirm: () => {
            formHandler({
              ...formCierreCuentasData,
              showModalProcedimientosPermitidos: false,
              showAlert: false,
            });
          },
        },
        showAlert: true,
        showModalProcedimientosPermitidos: false,
      })
    );

    return;
  }

  const procedimientosPermitidos =
    responseProcedimientosPermitidos?.data?.coberturasCierresCuentas?.map(
      (procedimiento) => {
        return {
          key: procedimiento.cobertura,
          cobertura: String(procedimiento.cobertura),
          nombreCobertura: procedimiento.descripcionCobertura,
          limiteContrato: procedimiento.limite || 0,
          tipoCobertura: procedimiento?.tipoCobertura,
        };
      }
    );

  const procedimiento = procedimientosPermitidos?.find(
    (item) => Number(item.cobertura) === Number(codigoProcedimiento)
  );

  if (!procedimiento) {
    yield put(
      IngresoActions.formCierreCuentasSuccess({
        ...formCierreCuentasData,
        alertConfig: {
          type: "info",
          title: "¡Hola!",
          message: MENSAJE_CIERRE_CUENTA.COBERTURA_SERVICIO_NO_PERMITIDA,
          onConfirm: () => {
            formHandler({
              ...formCierreCuentasData,
              showAlert: false,
              showModalProcedimientosPermitidos: false,
            });
          },
        },
        showAlert: true,
        showModalProcedimientosPermitidos: false,
      })
    );

    return;
  }

  const requestReclamantes = {
    tipoReclamante: tipoPss,
    reclamante: codigoPss,
    compania: compania,
  };

  const responserReclamantes = yield call(
    IngresoService.consultarReclamantes,
    requestReclamantes
  );

  const reclamante = responserReclamantes?.data?.reclamantes?.find(
    (item) => Number(item.codigo) === Number(codigoPss)
  );

  if (!reclamante) {
    yield put(
      IngresoActions.formCierreCuentasSuccess({
        ...formCierreCuentasData,
        alertConfig: {
          type: "info",
          title: "¡Hola!",
          message: `El reclamante con el código ${codigoPss} no existe`,
          onConfirm: () => {
            formHandler({
              ...formCierreCuentasData,
              showAlert: false,
              showModalProcedimientosPermitidos: false,
            });
          },
        },
        showAlert: true,
        showModalProcedimientosPermitidos: false,
      })
    );

    return;
  }

  const requestValidar = {
    ano: Number(anio),
    compania: Number(compania),
    ramo: Number(ramo),
    secuencial: Number(secuencialIngreso),
    secuenciaPoliza: Number(secuenciaPoliza),
    plan: Number(codigoPlan),
    servicio: Number(tipoServicio),
    codigoAsegurado: Number(codigoAsegurado),
    secuenciaAsegurado: Number(secuenciaAsegurado),
    tipoAsegurado: tipoAsegurado,
    fechaSalida: fechaSalida,
    cobertura: Number(procedimiento?.cobertura),
    fechaServicio: fechaServicio,
    frecuencia: Number(frecuencia),
    sexo: sexo,
    estadoCivil: estadoCivil,
    categoriaAsegurado: categoriaAfiliado,
    fechaNacimiento: moment(fechaNacimiento).format(
      DATE_FORMATS.middledash_ymd
    ),
    tipoReclamante: tipoPss,
    reclamante: Number(codigoPss),
    checkCirugia: null,
    montoReclamado:
      !isEmptyValue(montoReclamado) && Number(montoReclamado) > 0
        ? Number(montoReclamado)
        : null,
  };

  const responseValidar = yield call(
    IngresoService.validarCoberturaIngreso,
    requestValidar
  );

  if (responseValidar?.respuesta?.codigo !== 0) {
    yield put(
      IngresoActions.formCierreCuentasSuccess({
        ...formCierreCuentasData,
        alertConfig: {
          type: "info",
          title: "¡Hola!",
          message:
            responseValidar?.respuesta?.mensaje ||
            ResponseCode.BAD_REQUEST.message,
          onConfirm: () => {
            formHandler({
              ...formCierreCuentasData,
              showAlert: false,
              showModalProcedimientosPermitidos: false,
            });
          },
        },
        showAlert: true,
        showModalProcedimientosPermitidos: false,
      })
    );
    return;
  }

  const montoLimiteCobertura = responseValidar?.data?.limiteCobertura;

  if (
    montoLimiteCobertura &&
    montoLimiteCobertura > 0 &&
    Number(montoReclamado) > montoLimiteCobertura
  ) {
    yield put(
      IngresoActions.formCierreCuentasSuccess({
        ...formCierreCuentasData,
        alertConfig: {
          type: "info",
          title: "¡Hola!",
          message: MENSAJE_CIERRE_CUENTA.EXCEDE_MONTO_MAXIMO.replace(
            "codigoCobertura",
            codigoProcedimiento
          ),
          onConfirm: () => {
            formHandler({
              ...formCierreCuentasData,
              showAlert: false,
            });
          },
        },
        showAlert: true,
      })
    );

    return;
  }

  const secuencia = obtenerSiguienteSecuencia(
    formCierreCuentasData.procedimientos
  );

  const procedimientoValidado = {
    ...procedimiento,
    secuencia,
    tipoReclamante: tipoPss,
    codigoReclamante: codigoPss,
    nombreReclamante: nombrePss,
    limiteContrato: responseValidar?.data?.limiteAfiliado,
    frecuencia: responseValidar?.data?.frecuencia,
    montoReclamado: responseValidar?.data?.montoReclamado,
    montoAutorizado: responseValidar?.data?.montoPagar,
    montoDiferencia: responseValidar?.data?.diferenciaNoCubierta,
    montoCoaseguro: responseValidar?.data?.montoCoaseguro,
    coaseguro: responseValidar?.data?.porcientoCoaseguro,
    esNuevo: true,
  };

  const requestInsertar = {
    ano: anio,
    secuencial: secuencialIngreso,
    secuencia: secuencia === 0 || !secuencia ? 1 : secuencia,
    servicio: Number(tipoServicio),
    tipoCobertura: Number(procedimiento?.tipoCobertura),
    cobertura: Number(procedimiento?.cobertura),
    fechaServicio: fechaServicio,
    frecuencia: Number(frecuencia),
    montoReclamadoCobertura: Number(procedimientoValidado?.montoReclamado),
    reserva: Number(procedimientoValidado?.montoDiferencia),
    estatus: ESTATUS_COBERTURA.VIGENTE,
    limiteCobertura: Number(procedimiento?.limiteContrato),
    tipoAfiliado: tipoPss,
    codigoCentro: Number(codigoPss),
    porcientoCobertura: Number(procedimientoValidado?.coaseguro),
    usuarioTransaccion: USUARIO_WEB,
    fechaTransaccion: moment(new Date()).format(DATE_FORMATS.middledash_ymd),
    montoPagoCobertura: Number(procedimientoValidado?.montoAutorizado),
    ramoIngreso: Number(ramo),
    montoPagoAfiliado: Number(procedimientoValidado?.montoCoaseguro),
    excedenteCopago: Number(procedimientoValidado?.montoDiferencia),
    fechaIngresoCobertura: moment(new Date()).format(
      DATE_FORMATS.middledash_ymd
    ),
    usuarioIngresoCobertura: USUARIO_WEB,
    codigoCompania: Number(compania),
  };

  const insertarProcedimientoResponse = yield call(
    IngresoService.insertarCoberturaIngreso,
    requestInsertar
  );

  if (!insertarProcedimientoResponse?.data?.satisfactorio) {
    yield put(
      IngresoActions.formCierreCuentasSuccess({
        ...formCierreCuentasData,
        alertConfig: {
          type: "info",
          title: "¡Hola!",
          message: `No se pudo insertar el procedimiento ${procedimiento?.cobertura}`,
          onConfirm: () => {
            formHandler({
              ...formCierreCuentasData,
              showAlert: false,
              showModalProcedimientosPermitidos: false,
            });
          },
        },
        showAlert: true,
        showModalProcedimientosPermitidos: false,
      })
    );
    return;
  }

  const procedimientos = [
    ...formCierreCuentasData.procedimientos,
    procedimientoValidado,
  ];

  yield put(
    IngresoActions.formCierreCuentasSuccess({
      ...formCierreCuentasData,
      procedimientos: procedimientos,
      showModalProcedimientosPermitidos: false,
      codigoCobertura: "",
      frecuenciaCobertura: "",
      montoReclamado: "",
    })
  );
}

function* eliminarProcedimientoCierreCuentas(request) {
  yield put(IngresoActions.eliminarProcedimientoCierreCuentasLoading());

  const { formCierreCuentasData, formHandler, procedimiento } = request.payload;

  if (formCierreCuentasData?.procedimientos.length === 2) {
    yield put(
      IngresoActions.formCierreCuentasSuccess({
        ...formCierreCuentasData,
        alertConfig: {
          type: "info",
          title: "¡Hola!",
          message: MENSAJE_CIERRE_CUENTA.INGRESO_REQUIERE_PROCEDIMIENTOS,
          onConfirm: () => {
            formHandler({
              ...formCierreCuentasData,
              showAlert: false,
            });
          },
        },
        showAlert: true,
      })
    );

    return;
  }

  const { anio, compania, ramo, secuencialIngreso } = formCierreCuentasData;

  const requestEliminar = {
    ano: Number(anio),
    compania: Number(compania),
    ramo: Number(ramo),
    secuencial: Number(secuencialIngreso),
    cobertura: Number(procedimiento?.cobertura),
  };

  const response = yield call(
    IngresoService.eliminarCoberturaIngreso,
    requestEliminar
  );

  if (!response?.data?.satisfactorio) {
    yield put(
      IngresoActions.formCierreCuentasSuccess({
        ...formCierreCuentasData,
        alertConfig: {
          type: "info",
          title: "¡Hola!",
          message:
            "Ocurrió un error inesperado al intentar eliminar el procedimiento.",
          onConfirm: () => {
            formHandler({
              ...formCierreCuentasData,
              showAlert: false,
            });
          },
        },
        showAlert: true,
      })
    );

    return;
  }

  yield put(
    IngresoActions.formCierreCuentasSuccess({
      ...formCierreCuentasData,
      procedimientos: formCierreCuentasData.procedimientos?.filter(
        (item) => item.key !== procedimiento?.key
      ),
    })
  );
}

function* consultarReclamantes(request) {
  yield put(IngresoActions.consultarReclamantesLoading());

  const { formCierreCuentasData, compania, tipoReclamante, codigoReclamante } =
    request.payload;

  const requestReclamantes = {
    tipoReclamante: tipoReclamante,
    reclamante: codigoReclamante,
    compania: compania,
  };

  yield delay(500);

  const response = yield call(
    IngresoService.consultarReclamantes,
    requestReclamantes
  );

  const reclamante = response?.data?.reclamantes?.find(
    (item) => Number(item.codigo) === Number(codigoReclamante)
  );

  yield put(
    IngresoActions.formCierreCuentasSuccess({
      ...formCierreCuentasData,
      codigoPss: codigoReclamante,
      nombrePss: reclamante?.nombre || "",
    })
  );
}

function* cerrarCuentaClinica(request) {
  yield put(IngresoActions.cerrarCuentaClinicaLoading());

  const {
    formCierreCuentasData,
    tipoReclamante,
    codigoReclamante,
    formHandler,
    callBack,
  } = request.payload;

  const {
    tipoServicio,
    anio,
    compania,
    ramo,
    secuencialIngreso,
    tipoAsegurado,
    codigoAsegurado,
    secuenciaAsegurado,
    numeroPlastico,
    secuenciaPoliza,
    codigoPlan,
    fechaSalida,
  } = formCierreCuentasData;

  const requestCerrarCuenta = {
    ano: Number(anio),
    secuencial: secuencialIngreso,
    tipoReclamante: tipoReclamante,
    codigoReclamante: Number(codigoReclamante),
    tipoAsegurado: tipoAsegurado,
    codigoAsegurado: codigoAsegurado,
    frecuenciaAsegurado: secuenciaAsegurado,
    numeroPlastico: numeroPlastico,
    codigoCompania: Number(compania),
    ramoPoliza: Number(ramo),
    secuencialPoliza: Number(secuenciaPoliza),
    plan: Number(codigoPlan),
    fechaDia: fechaSalida,
    fechaTransaccion: moment(new Date()).format(DATE_FORMATS.middledash_ymd),
    user: USUARIO_WEB,
    servicio: Number(tipoServicio),
    riesgoLaboral: null,
    sim: null,
    procesoComentario: COMENTARIO_POR_DEFECTO,
  };

  const cierreCuentasResponse = yield call(
    IngresoService.generarCierreCuenta,
    requestCerrarCuenta
  );

  if (cierreCuentasResponse?.data?.numeroAutorizacion) {
    yield put(
      IngresoActions.formCierreCuentasSuccess({
        ...formCierreCuentasData,
        alertConfig: {
          type: "success",
          title: "¡Cuenta clínica cerrada con éxito!",
          message: `Número Autorización: ${cierreCuentasResponse?.data?.numeroAutorizacion}`,
          onConfirm: () => {
            formHandler({
              ...formCierreCuentasData,
              showAlert: false,
            });
            callBack();
          },
        },
        showAlert: true,
      })
    );
    return;
  }

  yield put(
    IngresoActions.formCierreCuentasSuccess({
      ...formCierreCuentasData,
      alertConfig: {
        type: "info",
        title: "¡Hola!",
        message:
          "¡En estos momentos no es posible realizar el cierre de cuentas, favor intentar más tarde.",
        onConfirm: () => {
          formHandler({
            ...formCierreCuentasData,
            showAlert: false,
          });
        },
      },
      showAlert: true,
    })
  );
}

function* consultarDetalleCierreCuenta(request) {
  yield put(IngresoActions.consultarDetalleCierreCuentaLoading());

  const {
    formCierreCuentasData,
    reclamante,
    formHandler,
    compania,
    anioIngreso,
    numeroIngreso,
  } = request.payload;

  const requestConsultarIngreso = {
    ano: anioIngreso,
    compania: compania,
    ramo: RAMO_INGRESO.PRIMERA,
    secuencial: numeroIngreso,
    reclamante: reclamante?.codigo,
  };

  const ingresoResponse = yield call(
    IngresoService.obtenerIngreso,
    requestConsultarIngreso
  );

  if (ingresoResponse?.data?.ingresoEncabezado) {
    const { ingresoEncabezado, ingresoDetalle } = ingresoResponse.data;

    let procedimientosIngreso = [DEFAULT_ROW_PROCEDIMIENTOS_INGRESO];

    if (ingresoDetalle?.length > 0) {
      const procedimientosData = ingresoDetalle?.map((ingreso, index) => {
        const secuencia = index + 1;
        return {
          secuencia,
          key: ingreso.cobertura,
          cobertura: ingreso.cobertura,
          nombreCobertura: ingreso.descripcionCobertura,
          tipoReclamante: ingreso.tipoCentroMedico,
          codigoReclamante: ingreso.codigoCentroMedico,
          nombreReclamante: ingreso.nombreCentro,
          limiteContrato: ingreso.limiteCobertura || 0,
          frecuencia: ingreso.frecuencia,
          montoReclamado: ingreso.montoReclamadoCobertura || 0,
          montoAutorizado: ingreso.montoPagoCobertura || 0,
          montoDiferencia: ingreso.montoPagoAfiliado || 0,
          coaseguro: ingreso.porcientoCobertura || 0,
          honorarios: ingreso.excedenteCopago || 0,
          esNuevo: false,
        };
      });

      procedimientosIngreso = procedimientosIngreso.concat(procedimientosData);
    }

    const honorariosMedicos = procedimientosIngreso?.filter(
      (detalle) => detalle.tipoReclamante === PSS_TYPE.MEDICO
    );

    const tieneMedicos = honorariosMedicos?.length > 0;

    const requestConsultarReclamos = {
      anio: anioIngreso,
      secuencial: numeroIngreso,
    };

    const reclamosResponse = yield call(
      IngresoService.obtenerReclamosIngreso,
      requestConsultarReclamos
    );

    const medicosParticipantes = honorariosMedicos
      ?.map((item) => ({
        tipoReclamante: item.tipoReclamante,
        codigoReclamante: item.codigoReclamante,
        nombreReclamante: item.nombreReclamante,
        reclamo: reclamosResponse?.data?.reclamosIngreso?.find(
          (honorario) => honorario.codigoReclamante === item.codigoReclamante
        )?.secuencial,
        honorarios: honorariosMedicos?.filter(
          (honorario) => honorario.codigoReclamante === item.codigoReclamante
        ),
      }))
      .filter(
        (item, index, self) =>
          index ===
          self.findIndex(
            (t) =>
              t.tipoReclamante === item.tipoReclamante &&
              t.codigoReclamante === item.codigoReclamante &&
              t.nombreReclamante === item.nombreReclamante
          )
      );

    const objetoPoliza = obtenerObjetoPoliza(ingresoEncabezado?.poliza);

    yield put(
      IngresoActions.formCierreCuentasSuccess({
        ...formCierreCuentasData,
        numeroIngreso: numeroIngreso,
        secuencialIngreso: ingresoEncabezado?.secuencial,
        fechaSalida: moment(ingresoEncabezado?.fechaSalida).format(
          DATE_FORMATS.middledash_ymd
        ),
        motivoEgreso: ingresoEncabezado?.motivoEgreso,
        causaInternamiento: CAUSA_POR_DEFECTO,
        tipoPss: ingresoEncabezado?.tipoCentroMed,
        pss: ingresoEncabezado?.nombreCentro,
        codigoPss: ingresoEncabezado?.codigoCentro,
        nombrePss: ingresoEncabezado?.nombreCentro,
        categoriaPss: ingresoEncabezado?.categoria,
        numeroReclamacion: ingresoEncabezado?.numeroReclamacion,
        numeroPrecertificacion: ingresoEncabezado?.numeroPrecertificacion,
        descripcionEstatus: ingresoEncabezado?.estatusDesc,
        numeroAfiliado: ingresoEncabezado?.numeroPlan,
        cedula: ingresoEncabezado?.cedula,
        nss: ingresoEncabezado?.nss,
        nombreAfiliado: ingresoEncabezado?.nombreAfiliado,
        edad: getAge(ingresoEncabezado?.fechaNacimiento),
        fechaServicio: moment(ingresoEncabezado?.fechaIngreso).format(
          DATE_FORMATS.middledash_ymd
        ),
        tipoServicio: String(ingresoEncabezado?.codigoServicio),
        plan: ingresoEncabezado?.descripcionPlan,
        codigoPlan: ingresoEncabezado?.planPoliza,
        anio: ingresoEncabezado?.anio,
        compania: objetoPoliza?.compania,
        ramo: objetoPoliza?.ramo,
        secuenciaPoliza: objetoPoliza?.secuencial,
        codigoAsegurado: ingresoEncabezado?.codigoAsegurado,
        numeroPlastico: ingresoEncabezado?.numeroPlan,
        secuenciaAsegurado: ingresoEncabezado?.frecuenciaAsegurado,
        tipoAsegurado: ingresoEncabezado?.tipoAsegurado,
        fechaNacimiento: ingresoEncabezado?.fechaNacimiento,
        sexo: ingresoEncabezado?.sexo,
        estadoCivil: ingresoEncabezado?.estadoCivil,
        categoriaAfiliado: ingresoEncabezado?.categoriaAfiliado,
        procedimientos: procedimientosIngreso,
        tieneMedicos,
        medicosParticipantes,
        aplicaExcepcion:
          Number(ingresoEncabezado?.codigoServicio) ===
            SERVICIO_EXCEPCION.PARTO ||
          Number(ingresoEncabezado?.codigoServicio) ===
            SERVICIO_EXCEPCION.CESAREA,
      })
    );
  } else {
    yield put(
      IngresoActions.formCierreCuentasSuccess({
        ...formCierreCuentasData,
        alertConfig: {
          type: "info",
          title: "¡Hola!",
          message: MENSAJE_CIERRE_CUENTA.INGRESO_NO_EXISTE,
          onConfirm: () => {
            formHandler({
              ...formCierreCuentasData,
              showAlert: false,
            });
          },
        },
        showAlert: true,
      })
    );
  }
}

function* consultarCierresCuentas(request) {
  yield put(IngresoActions.consultarCierresCuentasLoading());

  const {
    formConsultaCierresCuentasData,
    formHandler,
    compania,
    reclamante,
    fechaInicio,
    fechaFin,
    numeroReclamacion,
    numeroAfiliado,
    fechaServicio,
    fechaSalida,
    pagina,
    tamanoPagina,
  } = request.payload;

  const requestCierres = {
    codigoReclamante: reclamante,
    compania: compania,
    fechaInicial: !isEmptyValue(fechaServicio) ? fechaServicio : fechaInicio,
    fechaFinal: !isEmptyValue(fechaServicio) ? fechaServicio : fechaFin,
    secuencial: isEmptyValue(numeroReclamacion) ? null : numeroReclamacion,
    asegurado: isEmptyValue(numeroAfiliado) ? null : numeroAfiliado,
    fechaServicio: isEmptyValue(fechaServicio) ? null : fechaServicio,
    fechaSalida: isEmptyValue(fechaSalida) ? null : fechaSalida,
    numeroPagina: pagina,
    tamanoPagina: tamanoPagina,
  };

  const response = yield call(
    IngresoService.consultarCierresCuentas,
    requestCierres
  );

  if (response?.data?.cierreCuentas?.length > 0) {
    yield put(
      IngresoActions.formConsultaCierresCuentasSuccess({
        ...formConsultaCierresCuentasData,
        dataCierres: response?.data,
        fechaInicio,
        fechaFin,
      })
    );
    return;
  }

  yield put(
    IngresoActions.formConsultaCierresCuentasSuccess({
      ...formConsultaCierresCuentasData,
      alertConfig: {
        type: "info",
        title: "¡Hola!",
        message: "No se encontraron datos.",
        onConfirm: () => {
          formHandler({
            ...formConsultaCierresCuentasData,
            showAlert: false,
            fechaInicio,
            fechaFin,
            dataCierres: null,
          });
        },
      },
      showAlert: true,
      fechaInicio,
      fechaFin,
      dataCierres: null,
    })
  );
  return;
}

const exportedArray = [
  takeLatest(IngresoTypes.FORM_CIERRE_CUENTAS, formCierreCuentas),
  takeLatest(
    IngresoTypes.FORM_CONSULTA_CIERRES_CUENTAS,
    formConsultaCierresCuentas
  ),
  takeLatest(IngresoTypes.CONSULTAR_INGRESO, consultarIngreso),
  takeLatest(IngresoTypes.CONSULTAR_RECLAMANTES, consultarReclamantes),
  takeLatest(IngresoTypes.CONSULTAR_CIERRES_CUENTAS, consultarCierresCuentas),
  takeLatest(
    IngresoTypes.CONSULTAR_DETALLE_CIERRE_CUENTA,
    consultarDetalleCierreCuenta
  ),
  takeLatest(IngresoTypes.CERRAR_CUENTA_CLINICA, cerrarCuentaClinica),
  takeLatest(
    IngresoTypes.VALIDAR_PROCEDIMIENTO_CIERRE_CUENTAS,
    validarProcedimientoCierreCuentas
  ),
  takeLatest(
    IngresoTypes.ELIMINAR_PROCEDIMIENTO_CIERRE_CUENTAS,
    eliminarProcedimientoCierreCuentas
  ),
  takeLatest(
    IngresoTypes.GET_PROCEDIMIENTOS_PERMITIDOS_CIERRE_CUENTAS,
    getProcedimientosPermitidosCierreCuentas
  ),
];

export default exportedArray;
